import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getFeatures, getSimulation } from '../client';
import { computeEarningsChart, computeEarningsRateChart, PointInTimeEarnings } from '../earnings';
import { useSimulationId } from './useSimulationId';

export function useSimulationEarnings() {
  const simId = useSimulationId();
  const [tick, setTick] = useState(0);

  const simQuery = useQuery({
    queryKey: ['simulation', simId],
    queryFn: () => getSimulation({ path: { sim_id: simId }, throwOnError: true }).then(({ data }) => data),
    refetchInterval: 30_000,
  });

  const featuresQuery = useQuery({
    queryKey: ['features', simId],
    queryFn: () => getFeatures({ path: { sim_id: simId }, throwOnError: true }).then(({ data }) => data),
    refetchInterval: 30_000,
  });

  // Re-render every 3s while simulation is running to grow the chart line
  useEffect(() => {
    if (simQuery.data?.state !== 'running') return;
    const id = setInterval(() => setTick((t) => t + 1), 3_000);
    return () => clearInterval(id);
  }, [simQuery.data?.state]);

  const earningsData = useMemo(():
    | { chart_data: PointInTimeEarnings[]; rate_data: PointInTimeEarnings[] }
    | undefined => {
    void tick; // Tick dependency drives recomputation while simulation runs
    if (!simQuery.data || !featuresQuery.data) return undefined;

    // Interpolate simulation time client-side for smooth chart growth
    const sim = simQuery.data;
    const liveTimeS =
      sim.state === 'running' && sim.current_run_start_at
        ? sim.cumulative_time_s + (Date.now() - Date.parse(sim.current_run_start_at)) / 1000
        : sim.simulation_time_s;

    return {
      chart_data: computeEarningsChart(featuresQuery.data, liveTimeS),
      rate_data: computeEarningsRateChart(featuresQuery.data, liveTimeS),
    };
  }, [simQuery.data, featuresQuery.data, tick]);

  return {
    data: earningsData,
    isLoading: simQuery.isLoading || featuresQuery.isLoading,
    isError: simQuery.isError || featuresQuery.isError,
    error: simQuery.error || featuresQuery.error,
  };
}
